import React, { useState } from 'react';
import { ArrowLeft, Mail, MessageSquare, Clock, Send, CheckCircle, Loader2 } from 'lucide-react';
import api from '../services/api';

const Support = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('general');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/support/contact', { name, email, subject, message });
    } catch (err) {
      // Still show success - we store the request regardless
    }
    setLoading(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6" />
          <h1 className="text-2xl font-bold mb-4">Message Sent</h1>
          <p className="text-gray-400 mb-6">
            Thank you for reaching out. We'll get back to you within 24-48 hours.
          </p>
          <a href="/" className="inline-block px-6 py-3 bg-cyan-400 text-black rounded-lg font-medium hover:bg-cyan-300 transition-colors">
            Back to Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <a href="/" className="inline-flex items-center space-x-2 text-gray-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Home</span>
        </a>

        <h1 className="text-4xl font-bold mb-4">Support</h1>
        <p className="text-gray-400 mb-10">We're here to help. Reach out and we'll get back to you as soon as possible.</p>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <Mail className="h-8 w-8 text-cyan-400 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Email Us</h3>
            <a href="mailto:contact@pulsemedia.vip" className="text-cyan-400 hover:underline text-sm">contact@pulsemedia.vip</a>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <MessageSquare className="h-8 w-8 text-cyan-400 mb-4" />
            <h3 className="text-lg font-semibold mb-2">FAQ</h3>
            <p className="text-gray-400 text-sm">Check common questions below before reaching out.</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <Clock className="h-8 w-8 text-cyan-400 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Response Time</h3>
            <p className="text-gray-400 text-sm">We typically respond within 24-48 hours.</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <h2 className="text-2xl font-semibold mb-6">Send a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 transition-colors"
                  data-testid="support-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 transition-colors"
                  data-testid="support-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Topic</label>
                <select
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white focus:outline-none focus:border-cyan-400 transition-colors"
                  data-testid="support-subject-select"
                >
                  <option value="general">General Inquiry</option>
                  <option value="billing">Billing & Subscription</option>
                  <option value="technical">Technical Issue</option>
                  <option value="content">Content Request</option>
                  <option value="account">Account Issue</option>
                  <option value="feedback">Feedback</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Message</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="How can we help?"
                  rows={5}
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 transition-colors resize-none"
                  data-testid="support-message-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center space-x-2 py-3 rounded-lg bg-cyan-400 text-black font-semibold hover:bg-cyan-300 transition-colors disabled:opacity-50"
                data-testid="support-submit-button"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Send className="h-5 w-5" /><span>Send Message</span></>}
              </button>
            </form>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-6">Frequently Asked Questions</h2>
            <div className="space-y-4">
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h3 className="font-medium mb-2">How do I cancel my subscription?</h3>
                <p className="text-gray-400 text-sm">Go to your Profile page and select "Manage Subscription." You can cancel anytime, and your access continues through the end of your billing period.</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h3 className="font-medium mb-2">How much does Pulse Media cost?</h3>
                <p className="text-gray-400 text-sm">$1.99/month for unlimited access to our entire library. No ads, no hidden fees, no tiers.</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h3 className="font-medium mb-2">What devices are supported?</h3>
                <p className="text-gray-400 text-sm">Pulse Media is available on web browsers at pulseportal.tv and on Android via the Pulse Media app. iOS support is coming soon.</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h3 className="font-medium mb-2">How do I delete my account?</h3>
                <p className="text-gray-400 text-sm">Visit <a href="/delete-account" className="text-cyan-400 hover:underline">pulseportal.tv/delete-account</a> to submit an account deletion request. All data is removed within 30 days.</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h3 className="font-medium mb-2">I'm having trouble with playback</h3>
                <p className="text-gray-400 text-sm">Try refreshing the page, clearing your browser cache, or switching to a different browser. If the issue persists, contact us with details about your device and browser.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Support;
